import { useLayoutEffect, useRef, useState } from 'react';
import { unsaved, type UnsavedForm } from '../unsaved.ts';

export interface UnsavedOptions extends Omit<UnsavedForm, 'save' | 'discard'> {
  /** The form's own Save, the same function its button calls. */
  onSave: () => unknown;
  /** Put the fields back as they were loaded. */
  onDiscard: () => void;
}

let made = 0;

/**
 * How a form with a Save button says what it has not saved (P-29; design study 28, A): the fields changed, by their
 * labels, and its own Save and Discard. While any are named, the one bar at the window's foot (App's `UnsavedChanges`)
 * names them with Discard and Save, and leaving the page asks first; none, or the form gone, and the bar goes. The bar's
 * Save and Discard always call the form's latest, so a field committed as the button is pressed is in what is saved.
 */
export function useUnsaved({ onSave, onDiscard, ...rest }: UnsavedOptions): void {
  // One per form as mounted: a form filled again (a save re-keys it) is a new one.
  const [id] = useState(() => `form-${++made}`);
  const latest = useRef({ onSave, onDiscard });
  const [calls] = useState(() => ({ save: () => void latest.current.onSave(), discard: () => latest.current.onDiscard() }));
  // Told after every render (the store passes over what has not changed), before anything can be pressed.
  useLayoutEffect(() => {
    latest.current = { onSave, onDiscard };
    unsaved.set(id, { ...rest, ...calls });
  });
  useLayoutEffect(() => () => unsaved.set(id, null), [id]);
}
